<template>
  <div class="report-form">
    <h1>{{ t("Report Illegal Content") }}</h1>
    <p>{{ t("Ersties Team is highly committed and works continuously on the removal of any content that violates our Terms of Service.") }}</p>
    <p>
      {{
        t(
          "If you come across uploaded material that is inappropriate, illegal, harmful or offensive, please report the content using the Abuse form below.",
        )
      }}
    </p>
    <p>
      {{ t("Your complaint will be resolved and response provided within 5 business days.") }}
      <template v-if="getTld() === 'net'">
        {{ t("In the case of disagreement, we will name a third party to arbitrate the issue.") }}
      </template>
    </p>

    <h3>{{ t("ARE YOU DEPICTED IN A VIDEO ? PLEASE USE THIS FORM AS WELL!") }}</h3>
    <p>
      {{ t("If you are a copyright owner who believes your content has been uploaded to this site without authorization, please") }}
      <router-link to="/dmca"> {{ t("click here") }} </router-link>.
    </p>
    <p>
      {{ t("Be sure to include the video URL and any information that enables us to swiftly identify the content and act on the report.") }}
    </p>
    <p>
      <strong>{{ t("Please identify the specific category of issue you are reporting(you must select one category)*:") }}</strong>
    </p>

    <div class="form">
      <label>
        <input v-model="category" type="radio" name="category" value="consent" required />
        {{ t("Consent (harassment / revenge porn / upload without consent / blackmail / exploitation)") }}
      </label>
      <label>
        <input v-model="category" type="radio" name="category" value="sharing_personal_info" required />
        {{ t("Sharing personal information (name, email, phone, ip address)") }}
      </label>
      <label>
        <input v-model="category" type="radio" name="category" value="other" required />
        {{ t("Any other issue") }} <a href="/prohibited_content" target="_blank">({{ t("Prohibited Content") }})</a>
      </label>

      <input v-model="name" type="text" name="name" :placeholder="t('Your name*')" required />
      <input v-model="email" type="email" name="email" :placeholder="t('Email*')" required />
      <textarea v-model="urls" name="urls" :placeholder="t('URLS of the content you’re reporting*')" rows="4" required />

      <p>
        <strong>{{ t("Are you depicted in this content?*") }}</strong>
      </p>
      <label>
        <input v-model="is_depicted" type="radio" name="is_depicted" value="1" required />
        {{ t("Yes") }}
      </label>
      <label>
        <input v-model="is_depicted" type="radio" name="is_depicted" value="0" required />
        {{ t("No") }}
      </label>

      <p>
        <strong>{{ t("Have you ever agreed to the distribution of this content?*") }}</strong>
      </p>
      <label>
        <input v-model="is_agreed" type="radio" name="is_agreed" value="1" required />
        {{ t("Yes") }}
      </label>
      <label>
        <input v-model="is_agreed" type="radio" name="is_agreed" value="0" required />
        {{ t("No") }}
      </label>

      <p>
        <strong>{{ t("Reason you report this content *") }}:</strong>
      </p>
      <textarea v-model="reason" name="reason" rows="4" />

      <p>
        <strong>{{ t("Digital signature *:") }}</strong>
      </p>
      <p class="note">
        {{ t("Please type your name") }}
      </p>
      <input v-model="signature_name" type="text" name="signature_name" required />

      <p>
        {{
          t(
            "By typing your name in the field below, you guarantee that you are the person being named and represented on this form and that all provided information is true and accurate.",
          )
        }}
      </p>

      <captcha :error="captchaError" @on-captcha-verify="onCaptchaVerify" @on-captcha-expired="onCaptchaExpired" />

      <submit-button :text="t('Send')" :busy="isLoading" :is-primary="true" :is-disabled="!canSubmit" @click="onSubmit" />

      <p v-if="error" class="error">
        {{ error }}
      </p>
      <p v-if="success" class="success">
        {{ t("Your report has been successfully sent.") }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { amplitudeTrack } from "@/utils/amplitude";
import SubmitButton from "@/components/submit-button";
import Captcha from "@/components/captcha";
import pagesService from "@/api/services/pagesService";
import { getTld } from "@/utils/urlHelper.ts";

defineOptions({
  name: "report-form",
});

const { locale, t } = useI18n();

const email = ref("");
const error = ref("");
const success = ref(false);
const isLoading = ref(false);

const category = ref("");
const name = ref("");
const urls = ref("");
const is_depicted = ref("");
const is_agreed = ref("");
const reason = ref("");
const signature_name = ref("");
const captchaValue = ref("");
const captchaError = ref("");

const canSubmit = computed(
  () =>
    category.value &&
    name.value &&
    email.value &&
    urls.value &&
    is_depicted.value &&
    is_agreed.value &&
    reason.value &&
    signature_name.value &&
    captchaValue.value,
);

const onSubmit = async () => {
  if (isLoading.value) {
    return;
  }

  error.value = "";
  isLoading.value = true;

  try {
    await pagesService.contentReport(
      category.value,
      name.value,
      email.value,
      urls.value,
      is_depicted.value,
      is_agreed.value,
      reason.value,
      captchaValue.value,
    );

    category.value = "";
    name.value = "";
    email.value = "";
    urls.value = "";
    is_depicted.value = "";
    is_agreed.value = "";
    reason.value = "";
    signature_name.value = "";
    captchaValue.value = "";

    success.value = true;
    amplitudeTrack("Report: send");
  } catch (e: any) {
    error.value = e.status?.message[locale.value] || t("Something went wrong");
  } finally {
    isLoading.value = false;
    captchaValue.value = "";
  }
};

const onCaptchaVerify = (token: string) => {
  captchaValue.value = token;
};

const onCaptchaExpired = () => {
  captchaValue.value = "";
};
</script>

<style lang="scss">
@import "./index";
</style>
